import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { X, CheckCircle2, AlertTriangle, Info } from "lucide-react";

const ToastContext = createContext(null);

const VARIANTS = {
  success: {
    title: "Thành công",
    wrap: "border-emerald-100 bg-white",
    iconWrap: "bg-emerald-50 text-emerald-700",
    progress: "bg-emerald-500",
    Icon: CheckCircle2,
  },
  error: {
    title: "Lỗi",
    wrap: "border-rose-100 bg-white",
    iconWrap: "bg-rose-50 text-rose-700",
    progress: "bg-rose-500",
    Icon: AlertTriangle,
  },
  info: {
    title: "Thông báo",
    wrap: "border-slate-100 bg-white",
    iconWrap: "bg-slate-50 text-slate-700",
    progress: "bg-slate-500",
    Icon: Info,
  },
};

const SingleToast = ({ id, msg, type, onClose, durationMs = 3500 }) => {
  const v = VARIANTS[type] || VARIANTS.info;
  const { Icon } = v;
  const [progress, setProgress] = useState(100);

  React.useEffect(() => {
    const start = Date.now();
    const tick = () => {
      const elapsed = Date.now() - start;
      const next = Math.max(0, 100 - (elapsed / durationMs) * 100);
      setProgress(next);
      if (next <= 0) onClose(id);
    };
    const i = setInterval(tick, 75);
    return () => clearInterval(i);
  }, [durationMs, onClose]);

  return (
    <div className={`w-[340px] overflow-hidden rounded-2xl border shadow-lg ${v.wrap}`}>
      <div className="flex items-start gap-3.5 pl-5 pr-3 pt-4 pb-3">
        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${v.iconWrap}`}>
          <Icon size={22} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-[13px] font-bold tracking-tight text-text-main">{v.title}</p>
          <p className="mt-1 text-[13px] leading-snug text-text-soft">{msg}</p>
        </div>
        <button
          type="button"
          onClick={() => onClose(id)}
          aria-label="Đóng"
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-soft transition hover:bg-bg-section hover:text-text-main"
        >
          <X size={16} strokeWidth={2} />
        </button>
      </div>

      <div className="mx-4 mb-3 h-1 overflow-hidden rounded-full bg-bg-section">
        <div className={`h-full rounded-full ${v.progress} transition-[width] duration-75 ease-linear`} style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
};

const ToastStack = ({ toasts, onClose }) => (
  <div className="pointer-events-none fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-3 sm:bottom-8 sm:right-8" aria-live="polite">
    {toasts.map((t) => (
      <div key={t.id} className="pointer-events-auto">
        <SingleToast {...t} onClose={onClose} />
      </div>
    ))}
  </div>
);

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((msg, type = "success", durationMs = 3500) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, msg, type, durationMs }]);
    return id;
  }, []);

  const value = useMemo(() => ({ push }), [push]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onClose={remove} />
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within <ToastProvider />");
  }
  return ctx;
};

